// 智慧物流解决方案 — 页面全部文案与配置集中管理，视图只负责渲染

export const hero = {
  eyebrow: '产品与解决方案 · 智慧物流',
  title: '让每一件货物在 AIoT 网络中自动流转',
  desc: '以智能视觉监控、商用显示与智慧物联三大硬件底座，叠加 ePin 一物一码与九畴云市供应链平台，为仓储、运输、园区与末端配送构建端到端可感知、可调度、可追溯的数智化物流体系。',
  chips: ['端-边-云 一体架构', '全链路 可视可控', '一物一码 可信溯源', '复用 县域共配 能力'],
  primaryLabel: '获取方案白皮书',
  primaryHref: '/contact/business',
  secondaryLabel: '查看系统架构',
  secondaryHref: '#architecture',
  image:
    'https://readdy.ai/api/search-image?query=Realistic%20photograph%20of%20a%20modern%20smart%20logistics%20warehouse%20interior%20with%20tall%20shelving%20racks%20full%20of%20parcels%2C%20automated%20conveyor%20sorting%20lines%2C%20small%20AI%20surveillance%20cameras%20mounted%20on%20the%20ceiling%2C%20warm%20daylight%20through%20high%20windows%2C%20wide%20angle%20professional%20industrial%20photography%2C%20clean%20composition%2C%20high%20detail&width=1920&height=1080&seq=logi-hero-01&orientation=landscape',
  stats: [
    { value: '2000亿+', title: '2025 全国年快递量', sub: '智能化已成必选项' },
    { value: '98%+', title: 'AI 视觉 + IoT', sub: '库存准确率' },
    { value: '40%↑', title: '智能分拣', sub: '效率提升' },
    { value: '秒级', title: '冷链 / 安防', sub: '异常自动告警' },
  ],
};

export const overview = {
  eyebrow: '方案概述',
  title: '从“人找货”到“货找人”的物流重构',
  desc: '当快递量仅靠加人加车已难以为继，物流行业的竞争焦点转向感知密度、调度智能与全链路可信。华辉数智不重造机器人本体，而是用自有 AIoT 硬件与平台能力，把“看得见、连得上、算得准、追得回”四件事落到每一个物流节点。',
  cards: [
    {
      tag: 'VISION',
      icon: 'ri-eye-line',
      title: '看得见',
      desc: 'AI 摄像机覆盖仓内作业、月台、周界，自动识别车牌、包裹破损、作业合规与人员/叉车安全。',
    },
    {
      tag: 'IoT SENSING',
      icon: 'ri-router-line',
      title: '连得上',
      desc: '温湿度、称重、RFID/BLE 定位标签与边缘网关，把货架、车辆、资产接入统一物联平台。',
    },
    {
      tag: 'ePin TRACE',
      icon: 'ri-qr-code-line',
      title: '追得回',
      desc: '一物一码贯穿仓储物流节点，扫码留痕不可覆盖，异常冻结与召回全过程可审计。',
    },
  ],
};

export const productMatrix = {
  eyebrow: '用到的产品',
  title: '本方案调用的产品矩阵',
  lead: '智慧物流不是孤立系统，而是对既有产品能力的场景化组合。以下为方案直接调用的硬件与平台能力。',
  items: [
    {
      icon: 'ri-camera-3-line',
      name: '智能视觉监控',
      en: 'Vision Monitoring',
      desc: 'AI 摄像机/球机，覆盖仓内作业、月台、周界',
      tags: ['视觉识别分拣', '车牌识别', '作业合规', '周界安防'],
      href: '/products',
    },
    {
      icon: 'ri-tv-2-line',
      name: '商用显示',
      en: 'Commercial Display',
      desc: 'LED 拼接屏与数字标牌',
      tags: ['调度指挥中心', '分拣引导', '园区看板', '末端取件'],
      href: '/products',
    },
    {
      icon: 'ri-cpu-line',
      name: '智慧物联',
      en: 'IoT Sensing',
      desc: '传感器/标签/边缘网关，构建设备感知底座',
      tags: ['冷链监控', '资产定位', '智能货架', '边缘网关'],
      href: '/products',
    },
    {
      icon: 'ri-qr-code-line',
      name: 'ePin 食安码',
      en: 'One-Code Trace',
      desc: '一物一码平台，仓储物流节点留痕',
      tags: ['节点留痕', '异常冻结', '召回审计', '可信身份'],
      href: '/cases/epin-food-safety',
    },
    {
      icon: 'ri-store-3-line',
      name: '九畴云市 / 中仙数智商贸',
      en: 'Supply Chain Hub',
      desc: '供应链协同中台，打通县乡村节点',
      tags: ['统仓统配', '数字货架', '云铺协同', '结算清分'],
      href: '/jiuchou',
    },
    {
      icon: 'ri-parking-box-line',
      name: '自由流停车',
      en: 'Free-Flow Parking',
      desc: '无感出入/车牌识别复用至物流园区',
      tags: ['无感出入', '月台排队', '装卸调度', '园区安防'],
      href: '/solutions/smart-parking',
    },
  ],
};

export const scenes = {
  eyebrow: '使用场景',
  title: '覆盖仓储 · 运输 · 园区 · 冷链 · 末端的全场景',
  subtitle: '方案以模块化方式适配不同物流节点，可按仓库、园区或产业带独立部署、逐步扩展。',
  items: [
    {
      id: 'sc-warehouse',
      icon: 'ri-archive-2-line',
      title: '智能仓储与分拣中心',
      image:
        'https://readdy.ai/api/search-image?query=Realistic%20photograph%20of%20an%20automated%20e-commerce%20fulfillment%20warehouse%20sorting%20center%2C%20conveyor%20belts%20carrying%20parcels%2C%20barcode%20scanners%2C%20workers%20in%20safety%20vests%2C%20rows%20of%20shelving%2C%20bright%20industrial%20lighting%2C%20wide%20angle%2C%20high%20detail%2C%20professional%20photography&width=800&height=600&seq=logi-sc-warehouse-01&orientation=landscape',
      alt: '智能仓储与分拣中心场景',
      points: [
        '视觉识别分拣：深度学习读取条码/二维码，引导机器人精准分流',
        '智能货架实时库存：称重/光感传感器自动上报，免人工盘点',
        'AMR/AGV 协同：视觉 + SLAM 动态导航，避障转运',
        '调度大屏：出入库、波次、异常一屏总览',
      ],
    },
    {
      id: 'sc-park',
      icon: 'ri-building-4-line',
      title: '智慧物流园区',
      image:
        'https://readdy.ai/api/search-image?query=Realistic%20photograph%20of%20a%20modern%20logistics%20park%20with%20rows%20of%20white%20cargo%20trucks%20parked%20at%20loading%20docks%2C%20high%20warehouse%20buildings%2C%20entrance%20gate%20with%20camera%2C%20clear%20daylight%2C%20wide%20angle%2C%20high%20detail%2C%20professional%20industrial%20photography&width=800&height=600&seq=logi-sc-park-02&orientation=landscape',
      alt: '智慧物流园区场景',
      points: [
        '车辆无感出入：自由流停车能力实现车牌识别通行',
        '月台管理：到车排队、装卸时长、超时告警',
        '周界安防：AI 视觉入侵检测 + 电子围栏',
        '能耗看板：光伏/用电/碳排可视化（绿色低碳）',
      ],
    },
    {
      id: 'sc-coldchain',
      icon: 'ri-temp-cold-line',
      title: '冷链与生鲜物流',
      image:
        'https://readdy.ai/api/search-image?query=Realistic%20photograph%20of%20a%20cold%20chain%20warehouse%20interior%20with%20refrigerated%20storage%20and%20stacked%20crates%20of%20fresh%20produce%2C%20temperature%20sensor%20devices%2C%20cool%20lighting%2C%20workers%20in%20insulated%20jackets%2C%20wide%20angle%2C%20high%20detail%2C%20professional%20photography&width=800&height=600&seq=logi-sc-coldchain-03&orientation=landscape',
      alt: '冷链与生鲜物流场景',
      points: [
        '温湿度全程监控：异常自动告警，数据上链留痕',
        'ePin 溯源：产地到餐桌一物一码可验',
        '品质分级：光谱/视觉辅助成熟度与破损分级',
        '应急：异常批次一键冻结、定向召回',
      ],
    },
    {
      id: 'sc-transit',
      icon: 'ri-truck-line',
      title: '运输在途与调度',
      image:
        'https://readdy.ai/api/search-image?query=Realistic%20photograph%20of%20a%20fleet%20of%20cargo%20trucks%20driving%20on%20a%20modern%20highway%20at%20daytime%2C%20logistics%20transport%20concept%2C%20clear%20sky%2C%20wide%20angle%2C%20high%20detail%2C%20professional%20transportation%20photography&width=800&height=600&seq=logi-sc-transit-04&orientation=landscape',
      alt: '运输在途与调度场景',
      points: [
        '在途监控：车载终端回传位置、温控、工况',
        'AI 路由优化：动态规划“车-货-路”最优匹配',
        '电子围栏：偏离路线、长时间滞留实时预警',
        '供应链风控：天气/交通/灾害自动调备货方案',
      ],
    },
    {
      id: 'sc-parcel',
      icon: 'ri-inbox-archive-line',
      title: '末端配送与取件',
      image:
        'https://readdy.ai/api/search-image?query=Realistic%20photograph%20of%20a%20modern%20smart%20parcel%20pickup%20station%20with%20rows%20of%20automated%20lockers%20and%20a%20digital%20display%20screen%2C%20bright%20clean%20indoor%20setting%2C%20a%20person%20scanning%20a%20code%2C%20wide%20angle%2C%20high%20detail%2C%20professional%20photography&width=800&height=600&seq=logi-sc-parcel-05&orientation=landscape',
      alt: '末端配送与取件场景',
      points: [
        '无人驿站/智能柜：商用显示引导屏 + 取件通知',
        '末端网格：网格仓与配送员协同调度',
        '无接触交付：扫码取件、留痕可追溯',
        '逆向回流：退换货扫码入码、逆向可追',
      ],
    },
    {
      id: 'sc-county',
      icon: 'ri-community-line',
      title: '产业带 / 县域共配',
      image:
        'https://readdy.ai/api/search-image?query=Realistic%20photograph%20of%20a%20county-level%20distribution%20center%20with%20delivery%20vans%20and%20stacked%20goods%20in%20cartons%2C%20rural%20road%20and%20green%20fields%20in%20the%20background%2C%20clear%20daylight%2C%20wide%20angle%2C%20high%20detail%2C%20professional%20photography&width=800&height=600&seq=logi-sc-county-06&orientation=landscape',
      alt: '产业带与县域共配场景',
      badge: '华辉差异化优势',
      points: [
        '统仓统配：九畴云市打通县乡村节点',
        '数字货架：门店商品展示与数据回流',
        '千县万村供应链：上行下行一张网',
        '结算清分：多方协同、自动清分',
      ],
    },
  ],
};

export const architecture = {
  eyebrow: '物联网软硬件系统',
  title: '端 — 边 — 云 一体化技术架构',
  subtitle:
    '感知终端产生数据，边缘节点就近推理与预处理，平台层统一治理设备与业务，应用层输出可视化调度与溯源能力。四层通过标准 API 打通，避免数据孤岛。',
  layers: [
    {
      tag: '应用层',
      name: '业务应用',
      icon: 'ri-apps-2-line',
      items: ['仓储可视化', '运输调度', '园区安防', '冷链监控', 'ePin 溯源追溯', 'BI 经营看板'],
    },
    {
      tag: '平台层',
      name: '华辉数智平台 / 软件',
      icon: 'ri-stack-line',
      items: [
        'IoT 设备管理平台',
        '数字孪生引擎',
        'WMS/TMS 集成',
        'ePin 一物一码中台',
        '九畴云市供应链中台',
        'AI 调度大脑',
      ],
    },
    {
      tag: '边缘层',
      name: '边缘计算 / 软硬一体',
      icon: 'ri-cpu-line',
      items: ['边缘 AI 推理盒子', '智能视觉分析', 'LoRa/NB-IoT 网关', '5G/工业总线接入', '本地数据预处理', '安全隔离'],
    },
    {
      tag: '感知层',
      name: '感知终端 / 硬件（华辉自有产品为主）',
      icon: 'ri-radar-line',
      items: ['AI 摄像机/球机', '温湿度/环境传感器', 'RFID·BLE 定位标签', '智能地磁/称重', '智能货架', '车载终端/月台设备'],
    },
  ],
  systemsTitle: '核心软硬件系统清单',
  systemsNote: '便于对接与报价，清单可按项目规模按需组合。',
  systems: [
    {
      icon: 'ri-router-line',
      title: '华辉物联设备管理平台',
      type: 'HW + SW',
      desc: '统一接入摄像机、传感器、标签与网关，提供资产管理、状态监测与远程运维。',
    },
    {
      icon: 'ri-cpu-line',
      title: '边缘 AI 推理盒子',
      type: 'HW',
      desc: '在仓内/月台本地完成视觉推理与数据预处理，降低上云带宽，断网可续传。',
    },
    {
      icon: 'ri-eye-line',
      title: '智能视觉分析系统',
      type: 'SW',
      desc: '车牌识别、包裹破损检测、作业合规与周界入侵算法，对接大屏输出告警。',
    },
    {
      icon: 'ri-temp-cold-line',
      title: '冷链监控告警系统',
      type: 'SW',
      desc: '温湿度阈值管理、异常实时告警与上链留痕，联动 ePin 溯源做批次冻结。',
    },
    {
      icon: 'ri-map-pin-user-line',
      title: '资产/人员定位系统',
      type: 'HW + SW',
      desc: '基于 RFID·BLE 与地磁，实时追踪托盘、叉车、人员在库轨迹，优化拣货路径。',
    },
    {
      icon: 'ri-tv-2-line',
      title: '调度指挥中心大屏',
      type: 'HW',
      desc: '商用显示 LED 拼接屏，聚合 WMS/TMS/安防/冷链数据，一屏总览园区运营。',
    },
    {
      icon: 'ri-qr-code-line',
      title: 'ePin 一物一码溯源系统',
      type: 'SW',
      desc: '仓储物流节点扫码留痕，记录不可覆盖，异常冻结与召回全过程可审计。',
    },
    {
      icon: 'ri-store-3-line',
      title: '供应链协同中台',
      type: 'SW',
      desc: '九畴云市连接数字货架与云铺，支撑统仓统配、县域共配与多方结算清分。',
    },
  ],
};

export const value = {
  eyebrow: '价值成效',
  title: '可量化的运营收益',
  metrics: [
    { value: '98%+', label: '库存准确率', desc: 'AI 视觉 + IoT 融合' },
    { value: '40%↑', label: '智能分拣效率提升', desc: '视觉识别引导分流' },
    { value: '40–60%', label: '重复性搬运人工成本下降', desc: '自动化替代人工作业' },
    { value: '秒级', label: '安防 / 冷链异常自动响应', desc: '阈值告警自动联动' },
  ],
  note: '行业数据显示，自动化分拣使单点处理量提升 30% 以上、干线时效提升约 25%、车辆周转率提升约 30%；AI 视觉将库存识别准确率由传统条码的 75–80% 提升至 98–99%。详见京东物流、菜鸟、新华网等公开报道。',
};

export const referenceCase = {
  eyebrow: '参考实践',
  icon: 'ri-qr-code-line',
  title: 'ePin 食安码：让每一件农产品都有可信数字身份',
  desc: '从生产建档、质检赋码到仓储物流、门店销售与消费者扫码，ePin 食安码用「一物一码」把食品安全的每一环连成一条可信数据链，是智慧物流“追得回”能力的现成样板。',
  points: [
    '生产到消费全链路可查、可验、可追溯',
    '质检不合格商品自动禁止上架销售',
    '物流节点扫码留痕，记录不可覆盖',
    '异常冻结与召回全过程留档可审计',
  ],
  ctaLabel: '查看溯源档案示例',
  ctaHref: '/trace/passport',
  image:
    'https://readdy.ai/api/search-image?query=Realistic%20close-up%20of%20a%20fresh%20agricultural%20product%20package%20with%20a%20QR%20code%20traceability%20label%20being%20scanned%20by%20a%20smartphone%2C%20clean%20bright%20background%2C%20soft%20daylight%2C%20macro%20detail%2C%20professional%20product%20photography&width=800&height=600&seq=logi-case-epin-01&orientation=landscape',
};

export const cta = {
  title: '为你的仓库、园区或产业带，定制一套智慧物流方案',
  desc: '我们的方案顾问将结合你的业务场景，输出产品组合、系统架构与落地路线图。',
  primaryLabel: '获取方案白皮书',
  primaryHref: '/contact/business',
  secondaryLabel: '查看系统架构',
  secondaryHref: '#architecture',
};